import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import ImageIcon from '@mui/icons-material/ImageOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import EditIcon from '@mui/icons-material/AutoFixHighRounded';
import SendIcon from '@mui/icons-material/SendRounded';
import AttachFileIcon from '@mui/icons-material/AttachFileRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import EventIcon from '@mui/icons-material/EventRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffRounded';

import api from "../../services/api";

/* ─── Agent Blob ─────────────────────────────────────────── */
const agentBlobCSS = `
@keyframes imgAgentMorph {
  0%,100% { border-radius: 62% 38% 46% 54% / 60% 44% 56% 40%; }
  20%  { border-radius: 40% 60% 55% 45% / 48% 62% 38% 52%; }
  40%  { border-radius: 55% 45% 38% 62% / 42% 55% 45% 58%; }
  60%  { border-radius: 38% 62% 60% 40% / 58% 38% 62% 42%; }
  80%  { border-radius: 50% 50% 45% 55% / 52% 48% 52% 48%; }
}
@keyframes imgAgentGlow {
  0%,100% { box-shadow: 0 0 10px 2px rgba(112,18,128,0.5), 0 0 22px 4px rgba(112,18,128,0.18); }
  50%      { box-shadow: 0 0 18px 4px rgba(112,18,128,0.7), 0 0 38px 7px rgba(112,18,128,0.28); }
}
@keyframes imgAgentBreath {
  0%,100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
}
`;

function AgentBlob({ size = 32 }) {
    return (
        <>
            <style>{agentBlobCSS}</style>
            <Box sx={{
                width: size, height: size, flexShrink: 0,
                background: 'linear-gradient(145deg, #1a0a3c 0%, #3a1580 45%, #701280 100%)',
                animation: 'imgAgentMorph 6s ease-in-out infinite, imgAgentGlow 3s ease-in-out infinite, imgAgentBreath 4s ease-in-out infinite',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'default', userSelect: 'none',
            }}>
                <Typography sx={{
                    fontSize: size > 36 ? '0.38rem' : '0.32rem',
                    fontWeight: 800, letterSpacing: '0.03em',
                    background: 'linear-gradient(135deg, #E0CFFF, #fff)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    lineHeight: 1, textTransform: 'uppercase',
                }}>
                    Agent
                </Typography>
            </Box>
        </>
    );
}

const ASPECT_RATIOS = [
    { label: '1:1', size: '1024x1024', desc: 'Quadrato', w: 1, h: 1 },
    { label: '4:3', size: '1792x1024', desc: 'Landscape', w: 4, h: 3 },
    { label: '16:9', size: '1792x1024', desc: 'Wide', w: 16, h: 9 },
    { label: '9:16', size: '1024x1792', desc: 'Ritratto', w: 9, h: 16 },
];

const STYLES = [
    { key: 'vivid', suffix: '' },
    { key: 'natural', suffix: '' },
    { key: 'photorealistic', suffix: 'photorealistic, high definition, sharp details, professional photography quality' },
    { key: 'oil_painting', suffix: 'classic oil painting style, visible brushstrokes, rich deep colors, fine art texture' },
    { key: 'watercolor', suffix: 'watercolor painting style, soft washes, gentle color bleeding, delicate and airy feel' },
    { key: 'digital_illustration', suffix: 'modern digital illustration, clean lines, flat colors, polished graphic design aesthetic' },
    { key: 'cinematic', suffix: 'cinematic photography, dramatic lighting, film grain, widescreen look, movie-quality atmosphere' },
    { key: 'sketch', suffix: 'pencil sketch drawing, graphite pencil strokes, black and white, hand-drawn aesthetic' },
    { key: 'neon_cyberpunk', suffix: 'neon cyberpunk style, glowing neon lights, dark background, futuristic city, synthwave aesthetic' },
    { key: 'minimalist', suffix: 'minimalist style, clean composition, ample white space, limited color palette, simple shapes' },
    { key: 'pop_art', suffix: 'pop art style, bold flat colors, inspired by Andy Warhol, comic book aesthetic, vibrant graphic' },
    { key: 'impressionism', suffix: 'impressionist painting style, loose expressive brushwork, soft light effects, inspired by Monet or Renoir' },
];

const SUGGESTIONS_POOL = [
    'Professionista in ufficio moderno con schermo dati, stile corporate minimalista',
    'Logo astratto per startup tech, colori viola e arancione, sfondo scuro',
    'Team aziendale in riunione, stile fotografico professionale',
    'Infografica marketing digitale, design piatto, colori vivaci',
    'Prodotto su sfondo bianco, luce professionale da studio',
    'Skyline cittadino al tramonto con luci dorate, atmosfera cinematografica',
    'Illustrazione di un brand lifestyle minimalista, palette neutri e beige',
    'Campagna pubblicitaria per app mobile, colori accesi su sfondo scuro',
    'Persona che lavora in un café moderno, stile editorial lifestyle',
    'Pattern geometrico astratto per social media, colori vivaci e moderni',
    'Ufficio open space luminoso con team che collabora, stile corporate',
    'Mockup packaging prodotto cosmetico di lusso, sfondo nero',
    'Banner evento tech conference, stile futuristico neon su scuro',
    'Ritratto professionale con sfondo sfumato viola, look LinkedIn',
    'Illustrazione flat di strategia marketing con icone colorate',
    'Piatto gourmet su tavolo di marmo, fotografia food professionale',
    'Schermata app mobile su sfondo gradient moderno, stile promo',
    'Montagna al tramonto con nebbia, paesaggio inspirazionale',
    'Workspace creativo con laptop, notebook e caffè, vista dall\'alto',
    'Grafico di crescita business con stile infografica moderna e colorata',
];

function pickRandomSuggestions(n = 5) {
    const shuffled = [...SUGGESTIONS_POOL].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, n);
}

/* ─── Aspect Ratio Mini Preview ──────────────────────────── */
const RatioThumb = ({ w, h, active }) => {
    const MAX = 28;
    const isPort = h > w;
    const tw = isPort ? Math.round(MAX * w / h) : MAX;
    const th = isPort ? MAX : Math.round(MAX * h / w);
    return (
        <Box sx={{
            width: tw, height: th, flexShrink: 0,
            borderRadius: '3px',
            border: `1.5px solid ${active ? '#701280' : 'rgba(0,0,0,0.25)'}`,
            background: active ? 'rgba(112,18,128,0.13)' : 'rgba(0,0,0,0.05)',
            transition: 'all 0.15s',
        }} />
    );
};

/* ─── Shared Settings Panel ─────────────────────────────── */
const SettingsPanel = ({ size, setSize, style, setStyle }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
            <Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                    {t('image.settings_format')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                    {ASPECT_RATIOS.map(ar => {
                        const active = size === ar.label;
                        return (
                            <Box
                                key={ar.label}
                                onClick={() => setSize(ar.label)}
                                sx={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6,
                                    px: 1.2, py: 1,
                                    borderRadius: '10px',
                                    border: `1.5px solid ${active ? '#701280' : 'rgba(0,0,0,0.1)'}`,
                                    background: active ? 'rgba(112,18,128,0.07)' : '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    flex: '1 1 calc(25% - 8px)',
                                    minWidth: 52,
                                    '&:hover': { borderColor: '#701280', background: 'rgba(112,18,128,0.05)' },
                                }}
                            >
                                <RatioThumb w={ar.w} h={ar.h} active={active} />
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: active ? 700 : 500, color: active ? '#701280' : '#4A4A5A', lineHeight: 1 }}>
                                    {ar.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
            <FormControl size="small" fullWidth>
                <InputLabel>{t('image.settings_style')}</InputLabel>
                <Select value={style} label={t('image.settings_style')} onChange={(e) => setStyle(e.target.value)}>
                    {STYLES.map((s, i) => <MenuItem key={s.key} value={s.key}>{t('image.styles', { returnObjects: true })[i] || s.key}</MenuItem>)}
                </Select>
            </FormControl>
        </Box>
    );
};

/* ─── Agent Chat Message ─────────────────────────────────── */
const AgentMessage = ({ msg, onDownload, onContinue, onAddToCalendar }) => {
    const { t } = useTranslation();
    if (msg.role === 'user') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                <Box sx={{
                    maxWidth: '75%',
                    px: 2,
                    py: 1.2,
                    borderRadius: '18px 18px 4px 18px',
                    background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                    color: '#fff',
                    fontSize: '0.88rem',
                    lineHeight: 1.5,
                    boxShadow: '0 2px 8px rgba(124,58,237,0.2)',
                }}>
                    {msg.content}
                </Box>
            </Box>
        );
    }

    if (msg.type === 'error') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <AgentBlob size={32} />
                <Box sx={{
                    maxWidth: '80%', px: 2, py: 1.2,
                    borderRadius: '18px 18px 18px 4px',
                    background: '#FFF5F5',
                    border: '1px solid rgba(239,68,68,0.2)',
                    fontSize: '0.87rem', color: '#B91C1C', lineHeight: 1.5,
                }}>
                    ⚠️ {msg.content}
                </Box>
            </Box>
        );
    }

    if (msg.type === 'image') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                <AgentBlob size={32} />
                <Box sx={{ flex: 1, maxWidth: '85%' }}>
                    <Box sx={{
                        px: 2, py: 1.2, mb: 1,
                        borderRadius: '18px 18px 18px 4px',
                        background: '#fff',
                        border: '1px solid rgba(0,0,0,0.07)',
                        fontSize: '0.88rem', color: '#1C1C28', lineHeight: 1.5,
                    }}>
                        {msg.content}
                    </Box>
                    <Box sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                        <img src={msg.image_url} alt="Immagine generata" style={{ width: '100%', height: 'auto', display: 'block' }} />
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => onDownload(msg.image_url)}
                            sx={{
                                fontSize: '0.78rem', textTransform: 'none',
                                color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.12)',
                                borderRadius: '8px', px: 1.5, py: 0.5,
                                '&:hover': { background: 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            {t('image.edit_download')}
                        </Button>
                        <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={onContinue}
                            sx={{
                                fontSize: '0.78rem', textTransform: 'none',
                                color: '#7C3AED', border: '1px solid rgba(124,58,237,0.2)',
                                borderRadius: '8px', px: 1.5, py: 0.5,
                                '&:hover': { background: 'rgba(124,58,237,0.06)' },
                            }}
                        >
                            {t('image.edit_modify')}
                        </Button>
                        {onAddToCalendar && (
                            <Button
                                size="small"
                                startIcon={<EventIcon />}
                                onClick={() => onAddToCalendar({ url: msg.image_url, prompt: msg.content })}
                                sx={{
                                    fontSize: '0.78rem', textTransform: 'none',
                                    color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.25)',
                                    borderRadius: '8px', px: 1.5, py: 0.5,
                                    '&:hover': { background: 'rgba(14,165,233,0.06)' },
                                }}
                            >
                                {t('image.edit_add_cal')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <AgentBlob size={32} />
            <Box sx={{
                maxWidth: '80%',
                px: 2, py: 1.2,
                borderRadius: '18px 18px 18px 4px',
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.07)',
                fontSize: '0.88rem', color: '#1C1C28', lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
            }}>
                {msg.content}
            </Box>
        </Box>
    );
};

/* ─── Image Edit Agent ───────────────────────────────────── */
const ImageEditAgent = ({ size, setSize, style, setStyle, onAddToCalendar, initialImageUrl }) => {
    const { t } = useTranslation();
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imageBase64, setImageBase64] = useState('');
    const [imageMime, setImageMime] = useState('image/jpeg');
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const chatEndRef = useRef(null);
    const historyRef = useRef([]);

    const scrollToBottom = useCallback(() => {
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }, []);

    const handleImageFile = useCallback((file) => {
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const dataUrl = e.target.result;
            const base64 = dataUrl.split(',')[1];
            setUploadedImage(dataUrl);
            setImageBase64(base64);
            setImageMime(file.type);
            setMessages([]);
            historyRef.current = [];
            // Auto-trigger agent analysis
            sendMessage('', base64, file.type);
        };
        reader.readAsDataURL(file);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sendMessage = useCallback(async (text, b64Override = '', mimeOverride = '') => {
        const userText = text.trim();
        const b64 = b64Override || imageBase64;
        const mime = mimeOverride || imageMime;

        if (!userText && !b64) return;
        setLoading(true);

        if (userText) {
            const userMsg = { role: 'user', content: userText };
            setMessages(prev => [...prev, userMsg]);
            historyRef.current = [...historyRef.current, userMsg];
            scrollToBottom();
        }

        try {
            const resolvedSize = ASPECT_RATIOS.find(a => a.label === size)?.size || size;
            const resp = await api.post('/api/openai/image-agent/', {
                messages: historyRef.current.filter(m => m.role !== 'user' || m.content !== userText).slice(-10),
                user_message: userText,
                image_base64: b64,
                image_mime: mime,
                size: resolvedSize,
                quality: 'standard',
                style: style.toLowerCase(),
            });

            const data = resp.data;
            let agentMsg;
            if (data.type === 'image') {
                agentMsg = {
                    role: 'agent',
                    type: 'image',
                    content: data.message,
                    image_url: data.image_url,
                    dall_e_prompt: data.dall_e_prompt,
                };
            } else if (data.type === 'error') {
                agentMsg = { role: 'agent', type: 'error', content: data.message };
            } else {
                agentMsg = { role: 'agent', type: 'text', content: data.message };
            }
            setMessages(prev => [...prev, agentMsg]);
            if (data.type !== 'error') {
                historyRef.current = [...historyRef.current, { role: 'assistant', content: data.message }];
            }
            scrollToBottom();
        } catch (err) {
            if (err?.response?.status === 402) {
                window.dispatchEvent(new CustomEvent('admind_limit_exceeded', { detail: err.response.data || {} }));
                setLoading(false);
                return;
            }
            const detail = err?.response?.data?.error;
            const errMsg = { role: 'agent', type: 'error', content: detail || 'Si è verificato un errore. Riprova.' };
            setMessages(prev => [...prev, errMsg]);
        }
        setLoading(false);
    }, [imageBase64, imageMime, size, style, scrollToBottom]);

    useEffect(() => {
        if (!initialImageUrl) return;
        setMessages([]);
        historyRef.current = [];
        setUploadedImage(initialImageUrl);
        (async () => {
            try {
                let base64, mime;
                if (initialImageUrl.startsWith('data:')) {
                    const [header, b64] = initialImageUrl.split(',');
                    mime = header.split(';')[0].replace('data:', '') || 'image/png';
                    base64 = b64;
                } else {
                    const resp = await fetch(`/api/openai/download-image/?url=${encodeURIComponent(initialImageUrl)}`);
                    const blob = await resp.blob();
                    mime = blob.type || 'image/png';
                    base64 = await new Promise((res) => {
                        const reader = new FileReader();
                        reader.onload = () => res(reader.result.split(',')[1]);
                        reader.readAsDataURL(blob);
                    });
                }
                setImageBase64(base64);
                setImageMime(mime);
                sendMessage('', base64, mime);
            } catch (_) {}
        })();
    }, [initialImageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSend = () => {
        if (!inputText.trim() || loading) return;
        const text = inputText;
        setInputText('');
        sendMessage(text);
    };

    const handleDownload = async (url) => {
        const filename = `admind-edit-${Date.now()}.png`;
        if (url.startsWith('data:')) {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            const a = document.createElement('a');
            a.href = `/api/openai/download-image/?url=${encodeURIComponent(url)}`;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    const handleContinue = () => {
        setMessages(prev => [...prev, { role: 'agent', type: 'text', content: 'Certo! Cosa vorresti cambiare nell\'immagine?' }]);
        historyRef.current = [...historyRef.current, { role: 'assistant', content: 'Certo! Cosa vorresti cambiare nell\'immagine?' }];
        scrollToBottom();
    };

    const handleReset = () => {
        setUploadedImage(null);
        setImageBase64('');
        setMessages([]);
        historyRef.current = [];
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) handleImageFile(file);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden', backgroundColor: '#FAFAF8' }}>
            {/* LEFT SIDEBAR */}
            <Box sx={{
                width: 280,
                flexShrink: 0,
                borderRight: '1px solid rgba(0,0,0,0.07)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                backgroundColor: '#F5F4FF',
                p: 2.5,
                gap: 2,
            }}>
                <Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('image.edit_upload_label')}
                    </Typography>

                    {!uploadedImage ? (
                        <Box
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            sx={{
                                borderRadius: '14px',
                                border: `2px dashed ${isDragging ? '#7C3AED' : 'rgba(124,58,237,0.25)'}`,
                                background: isDragging ? 'rgba(124,58,237,0.05)' : 'rgba(124,58,237,0.03)',
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                minHeight: 140,
                                '&:hover': { background: 'rgba(124,58,237,0.07)', borderColor: 'rgba(124,58,237,0.4)' },
                            }}
                        >
                            <AddPhotoAlternateIcon sx={{ fontSize: '2rem', color: '#7C3AED', opacity: 0.6 }} />
                            <Typography sx={{ fontSize: '0.8rem', color: '#7C3AED', fontWeight: 600, textAlign: 'center' }}>
                                {t('image.edit_upload_btn')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B', textAlign: 'center' }}>
                                {t('image.edit_upload_hint')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: '#9B9BAA', textAlign: 'center' }}>
                                {t('image.edit_upload_formats')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Box sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(124,58,237,0.2)' }}>
                                <img src={uploadedImage} alt="Caricata" style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                                <IconButton
                                    onClick={handleReset}
                                    size="small"
                                    sx={{
                                        position: 'absolute', top: 6, right: 6,
                                        background: 'rgba(0,0,0,0.6)', color: '#fff',
                                        width: 24, height: 24,
                                        '&:hover': { background: 'rgba(0,0,0,0.8)' },
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: '0.8rem' }} />
                                </IconButton>
                            </Box>
                            <Button
                                size="small"
                                startIcon={<AttachFileIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    mt: 1, fontSize: '0.75rem', textTransform: 'none',
                                    color: '#7C3AED', border: '1px solid rgba(124,58,237,0.2)',
                                    borderRadius: '8px', px: 1.5, py: 0.5, width: '100%',
                                    '&:hover': { background: 'rgba(124,58,237,0.06)' },
                                }}
                            >
                                {t('image.edit_change')}
                            </Button>
                        </Box>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => e.target.files[0] && handleImageFile(e.target.files[0])}
                    />
                </Box>

                {uploadedImage && (
                    <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.07)', pt: 2 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B', mb: 1.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('image.edit_settings')}
                        </Typography>
                        <SettingsPanel
                            size={size} setSize={setSize}
                            style={style} setStyle={setStyle}
                        />
                    </Box>
                )}

                {!uploadedImage && (
                    <Box sx={{
                        mt: 'auto',
                        p: 2,
                        borderRadius: '12px',
                        background: 'rgba(124,58,237,0.06)',
                        border: '1px solid rgba(124,58,237,0.12)',
                    }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#7C3AED', fontWeight: 600, mb: 0.5 }}>
                            {t('image.edit_how_title')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B6B7B', lineHeight: 1.6 }}>
                            {t('image.edit_how_desc')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* MAIN CHAT AREA */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {!uploadedImage ? (
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        p: 4,
                    }}>
                        <Box sx={{
                            width: 80, height: 80, borderRadius: '22px',
                            background: 'rgba(124,58,237,0.08)',
                            border: '1px solid rgba(124,58,237,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <EditIcon sx={{ fontSize: '2.2rem', color: '#A78BFA', opacity: 0.7 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, color: '#1C1C28', fontSize: '1.1rem' }}>
                            {t('image.edit_empty_title')}
                        </Typography>
                        <Typography sx={{ color: '#6B6B7B', textAlign: 'center', maxWidth: 340, lineHeight: 1.6 }}>
                            {t('image.edit_empty_desc')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Messages */}
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column' }}>
                            {messages.length === 0 && loading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <AgentBlob size={32} />
                                    <Box sx={{
                                        px: 2, py: 1.2,
                                        borderRadius: '18px 18px 18px 4px',
                                        background: '#fff',
                                        border: '1px solid rgba(0,0,0,0.07)',
                                        display: 'flex', alignItems: 'center', gap: 1,
                                    }}>
                                        <CircularProgress size={14} sx={{ color: '#10B981' }} />
                                        <Typography sx={{ fontSize: '0.85rem', color: '#6B6B7B' }}>
                                            {t('image.edit_analyzing')}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            {messages.map((msg, i) => (
                                <AgentMessage
                                    key={i}
                                    msg={msg}
                                    onDownload={handleDownload}
                                    onContinue={handleContinue}
                                    onAddToCalendar={onAddToCalendar}
                                />
                            ))}
                            {loading && messages.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <AgentBlob size={32} />
                                    <Box sx={{
                                        px: 2, py: 1.2,
                                        borderRadius: '18px 18px 18px 4px',
                                        background: '#fff',
                                        border: '1px solid rgba(0,0,0,0.07)',
                                        display: 'flex', alignItems: 'center', gap: 1,
                                    }}>
                                        <CircularProgress size={14} sx={{ color: '#10B981' }} />
                                        <Typography sx={{ fontSize: '0.85rem', color: '#6B6B7B' }}>
                                            {t('image.edit_processing')}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            <div ref={chatEndRef} />
                        </Box>

                        {/* Input */}
                        <Box sx={{
                            borderTop: '1px solid rgba(0,0,0,0.07)',
                            p: 2,
                            background: '#fff',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-end',
                        }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={t('image.edit_placeholder')}
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                                multiline
                                maxRows={4}
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '14px',
                                        '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(124,58,237,0.3)' },
                                        '&.Mui-focused fieldset': { borderColor: '#7C3AED' },
                                    },
                                }}
                            />
                            <IconButton
                                onClick={handleSend}
                                disabled={loading || !inputText.trim()}
                                sx={{
                                    background: loading || !inputText.trim() ? 'rgba(0,0,0,0.06)' : 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                                    color: loading || !inputText.trim() ? '#aaa' : '#fff',
                                    borderRadius: '12px',
                                    width: 42, height: 42,
                                    flexShrink: 0,
                                    '&:hover': { background: 'linear-gradient(135deg, #6D28D9, #7C3AED)' },
                                }}
                            >
                                <SendIcon sx={{ fontSize: '1.1rem' }} />
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

/* ─── Calendar Dialog ─────────────────────────────────────── */
const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
};

/* ─── Main Component ─────────────────────────────────────── */
const ModernImageGenerator = () => {
    const { t, i18n } = useTranslation();
    const [mode, setMode] = useState('genera');
    const [initialImageUrl, setInitialImageUrl] = useState(null);

    // Shared settings
    const [size, setSize] = useState('1:1');
    const [style, setStyle] = useState('vivid');

    const resolveSize = (label) => {
        const ar = ASPECT_RATIOS.find(a => a.label === label);
        return ar ? ar.size : '1024x1024';
    };

    const resolveStyleSuffix = (key) => {
        const s = STYLES.find(st => st.key === key);
        return s ? s.suffix : '';
    };
    const suggestions = useMemo(() => {
        const pool = t('image.suggestions_pool', { returnObjects: true });
        const arr = Array.isArray(pool) ? pool : SUGGESTIONS_POOL;
        return [...arr].sort(() => Math.random() - 0.5).slice(0, 5);
    }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    // Generate mode state
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [images, setImages] = useState([]);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showSuggestions, setShowSuggestions] = useState(true);

    // Calendar dialog state
    const [calOpen, setCalOpen] = useState(false);
    const [calImage, setCalImage] = useState(null); // { url, prompt }
    const [calTitle, setCalTitle] = useState('');
    const [calDate, setCalDate] = useState(getTomorrow());
    const [calSaving, setCalSaving] = useState(false);
    const [calSnack, setCalSnack] = useState({ open: false, msg: '', severity: 'success' });

    const handleOpenCal = useCallback((img) => {
        setCalImage(img);
        setCalTitle(img.prompt || img.content || 'Immagine generata');
        setCalDate(getTomorrow());
        setCalOpen(true);
    }, []);

    const handleSaveToCal = async () => {
        if (!calTitle.trim() || !calDate) return;
        setCalSaving(true);
        try {
            await api.post('/api/openai/calendar/', {
                title: calTitle.trim(),
                content_type: 'image',
                scheduled_date: calDate,
                content_text: '',
                image_url: calImage?.url || '',
                status: 'planned',
            });
            setCalSnack({ open: true, msg: t('image.snack_cal_ok'), severity: 'success' });
            setCalOpen(false);
        } catch {
            setCalSnack({ open: true, msg: t('image.snack_cal_err'), severity: 'error' });
        }
        setCalSaving(false);
    };

    const handleGenerate = async () => {
        if (!prompt.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const suffix = resolveStyleSuffix(style);
            const finalPrompt = suffix ? `${prompt.trim()}. ${suffix}` : prompt.trim();
            const resp = await api.post('/api/openai/dalle-v2/', {
                prompt: finalPrompt,
                size: resolveSize(size),
                quality: 'standard',
                model: 'gpt-image-1',
                n: 1,
            });
            const imgUrl = resp.data?.url || resp.data?.image_url || resp.data?.data?.[0]?.url;
            if (imgUrl) {
                const newImg = { url: imgUrl, prompt: prompt.trim(), id: Date.now() };
                setImages(prev => [newImg, ...prev]);
                setSelected(newImg);
            } else {
                setError(t('image.error_no_image'));
            }
        } catch (err) {
            if (err?.response?.status === 402) {
                window.dispatchEvent(new CustomEvent('admind_limit_exceeded', { detail: err.response.data || {} }));
                setLoading(false);
                return;
            }
            setError(err?.response?.data?.error || t('image.error_default'));
        }
        setLoading(false);
    };

    const handleDownload = async (url) => {
        const filename = `admind-image-${Date.now()}.png`;
        if (url.startsWith('data:')) {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            const a = document.createElement('a');
            a.href = `/api/openai/download-image/?url=${encodeURIComponent(url)}`;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#FAFAF8' }}>
            {/* Mode Toggle Bar */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2.5,
                py: 1.5,
                borderBottom: '1px solid rgba(0,0,0,0.07)',
                background: '#fff',
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: '12px', background: 'rgba(0,0,0,0.04)' }}>
                    {[
                        { key: 'genera', label: t('image.mode_generate'), desc: t('image.mode_generate_desc') },
                    ].map(({ key, label, desc }) => (
                        <Button
                            key={key}
                            onClick={() => setMode(key)}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                fontWeight: mode === key ? 700 : 500,
                                color: mode === key ? (key === 'genera' ? '#10B981' : '#7C3AED') : '#6B6B7B',
                                background: mode === key ? '#fff' : 'transparent',
                                boxShadow: mode === key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                borderRadius: '8px',
                                px: 2, py: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': { background: mode === key ? '#fff' : 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            {label}
                            <Typography component="span" sx={{ fontSize: '0.7rem', color: '#9B9BAA', ml: 0.8, display: { xs: 'none', sm: 'inline' } }}>
                                {desc}
                            </Typography>
                        </Button>
                    ))}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {mode === 'genera' ? (
                    <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
                        {/* Generate Sidebar */}
                        <Box sx={{
                            width: 300, flexShrink: 0,
                            borderRight: '1px solid rgba(0,0,0,0.07)',
                            display: 'flex', flexDirection: 'column',
                            overflow: 'auto',
                            backgroundColor: '#F5F4FF',
                            p: 2.5, gap: 2,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                                <Box sx={{
                                    width: 36, height: 36, borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #10B981, #34D399)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <ImageIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, color: '#1C1C28', fontSize: '0.95rem' }}>{t('image.gen_title')}</Typography>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B' }}>{t('image.gen_subtitle')}</Typography>
                                </Box>
                            </Box>

                            <TextField
                                fullWidth
                                label={t('image.field_prompt')}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                multiline
                                rows={4}
                                size="small"
                                placeholder={t('image.prompt_placeholder')}
                                onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleGenerate(); }}
                            />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showSuggestions ? 1 : 0 }}>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {t('image.suggestions_label')}
                                    </Typography>
                                    <Tooltip title={showSuggestions ? t('image.suggestions_hide') : t('image.suggestions_show')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowSuggestions(v => !v)}
                                            sx={{ p: 0.4, color: showSuggestions ? '#10B981' : '#9B9BAA', '&:hover': { background: 'rgba(16,185,129,0.08)' } }}
                                        >
                                            {showSuggestions
                                                ? <VisibilityIcon sx={{ fontSize: '0.95rem' }} />
                                                : <VisibilityOffIcon sx={{ fontSize: '0.95rem' }} />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {showSuggestions && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                                        {suggestions.map(s => (
                                            <Box
                                                key={s}
                                                onClick={() => setPrompt(s)}
                                                sx={{
                                                    p: 1.2, borderRadius: '10px',
                                                    background: 'rgba(0,0,0,0.03)',
                                                    border: '1px solid rgba(0,0,0,0.07)',
                                                    cursor: 'pointer', transition: 'all 0.2s',
                                                    '&:hover': { background: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.2)' },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '0.75rem', color: '#4A4A5A', lineHeight: 1.4 }}>{s}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            <SettingsPanel
                                size={size} setSize={setSize}
                                style={style} setStyle={setStyle}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleGenerate}
                                disabled={loading || !prompt.trim()}
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                                sx={{
                                    py: 1.3,
                                    background: 'linear-gradient(135deg, #10B981, #34D399)',
                                    '&:hover': { background: 'linear-gradient(135deg, #059669, #10B981)', boxShadow: '0 0 20px rgba(16,185,129,0.3)' },
                                }}
                            >
                                {t(loading ? 'image.btn_generating' : 'image.btn_generate')}
                            </Button>

                            {images.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {t('image.history_label', { count: images.length })}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {images.map((img) => (
                                            <Box
                                                key={img.id}
                                                onClick={() => setSelected(img)}
                                                sx={{
                                                    borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
                                                    border: selected?.id === img.id ? '2px solid #10B981' : '2px solid rgba(0,0,0,0.07)',
                                                    transition: 'border-color 0.2s',
                                                }}
                                            >
                                                <img src={img.url} alt={img.prompt} style={{ width: '100%', height: 72, objectFit: 'cover', display: 'block' }} />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Generate Preview Area */}
                        <Box sx={{
                            flex: 1, overflow: 'auto', p: 3,
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: (selected || loading) ? 'flex-start' : 'center',
                            backgroundColor: '#FAFAF8',
                        }}>
                            {!selected && !loading && !error && (
                                <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{
                                        width: 80, height: 80, borderRadius: '22px',
                                        background: 'rgba(16,185,129,0.1)',
                                        border: '1px solid rgba(16,185,129,0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <ImageIcon sx={{ fontSize: '2.2rem', color: '#34D399', opacity: 0.5 }} />
                                    </Box>
                                    <Typography sx={{ color: '#6B6B7B' }}>{t('image.empty_title')}</Typography>
                                </Box>
                            )}

                            {loading && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
                                    <Box sx={{ position: 'relative', width: 80, height: 80 }}>
                                        <CircularProgress size={80} thickness={2} sx={{ color: '#10B981', position: 'absolute' }} />
                                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <AutoAwesomeIcon sx={{ color: '#34D399', fontSize: '1.6rem' }} />
                                        </Box>
                                    </Box>
                                    <Typography sx={{ color: '#6B6B7B' }}>{t('image.loading_title')}</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#6B6B7B', opacity: 0.7 }}>{t('image.loading_sub')}</Typography>
                                </Box>
                            )}

                            {error && !loading && (
                                <Box sx={{ p: 3, borderRadius: '16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', maxWidth: 500 }}>
                                    <Typography sx={{ color: '#EF4444' }}>❌ {error}</Typography>
                                </Box>
                            )}

                            {selected && !loading && (
                                <Box sx={{ width: '100%', maxWidth: 700 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography sx={{ fontWeight: 600, color: '#1C1C28', fontSize: '0.9rem' }}>{t('image.result_label')}</Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title={t('image.download')}>
                                                <IconButton
                                                    onClick={() => handleDownload(selected.url)}
                                                    sx={{
                                                        color: '#6B6B7B',
                                                        border: '1px solid rgba(0,0,0,0.10)',
                                                        borderRadius: '10px', p: 0.8,
                                                        '&:hover': { color: '#10B981', borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.06)' },
                                                    }}
                                                >
                                                    <DownloadIcon sx={{ fontSize: '1.1rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('image.regenerate')}>
                                                <IconButton
                                                    onClick={handleGenerate}
                                                    sx={{
                                                        color: '#6B6B7B',
                                                        border: '1px solid rgba(0,0,0,0.10)',
                                                        borderRadius: '10px', p: 0.8,
                                                        '&:hover': { color: '#10B981', borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.06)' },
                                                    }}
                                                >
                                                    <RefreshIcon sx={{ fontSize: '1.1rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                            {/* EDIT BUTTON — hidden temporarily, re-enable by removing display:'none' */}
                                            <Box sx={{ display: 'none' }}>
                                            <Tooltip title={t('image.edit_image_tip')}>
                                                <IconButton
                                                    onClick={() => { setInitialImageUrl(selected.url); setMode('modifica'); }}
                                                    sx={{
                                                        color: '#7C3AED',
                                                        border: '1px solid rgba(124,58,237,0.2)',
                                                        borderRadius: '10px', p: 0.8,
                                                        '&:hover': { color: '#7C3AED', borderColor: 'rgba(124,58,237,0.4)', background: 'rgba(124,58,237,0.06)' },
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: '1.1rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                            </Box>
                                            <Tooltip title={t('image.add_to_cal')}>
                                                <IconButton
                                                    onClick={() => handleOpenCal({ url: selected.url, prompt: selected.prompt })}
                                                    sx={{
                                                        color: '#0EA5E9',
                                                        border: '1px solid rgba(14,165,233,0.25)',
                                                        borderRadius: '10px', p: 0.8,
                                                        '&:hover': { color: '#0EA5E9', borderColor: 'rgba(14,165,233,0.5)', background: 'rgba(14,165,233,0.06)' },
                                                    }}
                                                >
                                                    <EventIcon sx={{ fontSize: '1.1rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    <Box sx={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}>
                                        <img src={selected.url} alt={selected.prompt} style={{ width: '100%', height: 'auto', display: 'block' }} />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <ImageEditAgent
                        size={size} setSize={setSize}
                        style={style} setStyle={setStyle}
                        onAddToCalendar={handleOpenCal}
                        initialImageUrl={initialImageUrl}
                    />
                )}
            </Box>

            {/* ── Calendar Dialog ── */}
            <Dialog
                open={calOpen}
                onClose={() => setCalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '20px', p: 0.5 } }}
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                        background: 'linear-gradient(135deg, #0EA5E9, #38BDF8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <EventIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1C1C28' }}>
                            {t('image.cal_title')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.73rem', color: '#6B6B7B' }}>
                            {t('image.cal_subtitle')}
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {calImage?.url && (
                        <Box sx={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', maxHeight: 160 }}>
                            <img src={calImage.url} alt="Preview" style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        size="small"
                        label={t('image.cal_field_title')}
                        value={calTitle}
                        onChange={(e) => setCalTitle(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label={t('image.cal_field_date')}
                        type="date"
                        value={calDate}
                        onChange={(e) => setCalDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setCalOpen(false)}
                        sx={{ textTransform: 'none', color: '#6B6B7B', borderRadius: '10px' }}
                    >
                        {t('image.cal_cancel')}
                    </Button>
                    <Button
                        onClick={handleSaveToCal}
                        variant="contained"
                        disabled={calSaving || !calTitle.trim() || !calDate}
                        startIcon={calSaving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                        sx={{
                            textTransform: 'none', borderRadius: '10px', px: 2.5,
                            background: 'linear-gradient(135deg, #0EA5E9, #38BDF8)',
                            '&:hover': { background: 'linear-gradient(135deg, #0284C7, #0EA5E9)' },
                        }}
                    >
                        {t(calSaving ? 'image.cal_saving' : 'image.cal_save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar feedback ── */}
            <Snackbar
                open={calSnack.open}
                autoHideDuration={4000}
                onClose={() => setCalSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setCalSnack(s => ({ ...s, open: false }))}
                    severity={calSnack.severity}
                    sx={{ borderRadius: '12px' }}
                >
                    {calSnack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ModernImageGenerator;
